import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';

/**
 *  TANULÓ KILÉPTETÉSE — távozás az iskolából.
 *
 *  Két dolog kell, és eddig egyik sem volt meg: (1) a besorolásokat le kell zárni,
 *  különben a tanuló ott marad a névsorokban és tovább jelölik hiányzónak; (2) a
 *  mulasztásait ki kell tudni íratni, mert a hivatalos napló az e-KRÉTA, és oda át
 *  kell vezetni, ami nálunk összegyűlt.
 *
 *  A képernyő ezért ELŐBB a kimutatást mutatja, és csak utána engedi a kiléptetést:
 *  ha van még elbírálatlan vagy igazolatlan óra, azt előbb rendezni kell — azzal a
 *  tudással kell a gombot megnyomni, nem utána szembesülni vele.
 */
@Component({
  selector: 'app-admin-tanulo-kilepes',
  templateUrl: './tanulo-kilepes.component.html',
  styleUrls: ['./tanulo-kilepes.component.scss'],
})
export class AdminTanuloKilepesComponent implements OnInit {
  intezmeny = '';
  betoltes = false;
  fut = false;
  hiba = '';

  tanulok: any[] = [];
  tanuloSzuro = '';
  valasztottTanulo: any = null;

  datum = new Date().toISOString().slice(0, 10);
  ok = '';
  /** A megszűnés oka kóddal (Nkt. 53. §, backend/kilepes-okok.js) és az új intézmény – a
   *  beírási napló 95. § (9) ag) rovatához; a szabad szöveg (ok) kiegészítés. */
  okKod = '';
  atvevo = '';
  okok: { kod: string; nev: string; jogalap: string; atvevo?: boolean }[] = [];
  /** A távozás napja UTÁNI hiányzás-jelöléseket levegyük-e. */
  utaniTorles = false;

  elemzes: any = null;
  /** A tételes lista alapból csukva: 100+ sor, és nem mindig kell. */
  tetelekNyitva = false;

  constructor(private http: HttpClient, private data: DataService, private route: ActivatedRoute, private router: Router) {}

  /**
   *  ISKOLAVÁLTÁS (2026-11-02, Nkt. 41. § (8a)): ha a megszűnés oka „Másik iskola átvette”, vagy meg
   *  van adva az új intézmény, az új iskolának át kell adni a tanuló előrehaladási adatait és az
   *  évközi jegyeit. A képernyő ilyenkor mutatja, elkészült-e már az átadó csomag (az elemzés
   *  `atado` mezője, backend/tanulo-atado.js), és egy gombbal az Iskolaváltás képernyőre visz.
   */
  get iskolavaltas(): boolean {
    return this.okKod === 'atvetel' || !!this.atvevo.trim();
  }

  atadoNyit() {
    if (!this.valasztottTanulo) return;
    this.router.navigate(['/admin/tanulo-atado'], { queryParams: { oktazon: this.valasztottTanulo.oktazon } });
  }

  async ngOnInit() {
    const si = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(si?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    try { this.okok = await firstValueFrom(this.http.get<any[]>('/api/tanulo-kilepes/okok')) || []; } catch { this.okok = []; }
    await this.tanulokBetoltese();
    /*  Előválasztás a hivatkozásból (?tanulo=…&ok_kod=…[&datum=…]): az Osztályfőnök › „Igazolatlan
     *  mulasztás – értesítések” kártya innen indítja a nem tanköteles tanuló kiléptetését, ha a
     *  jogviszony megszűnése megállapítható (20/2012. EMMI r. 50. § (4)). */
    const qp = this.route.snapshot.queryParamMap;
    const elo = this.tanulok.find((t) => t.oktazon === String(qp.get('tanulo') || ''));
    //  A Fegyelmi képernyő véglegessé vált kizárásnál a véglegessé válás napját is átadja (?datum=…).
    const qDatum = String(qp.get('datum') || '');
    if (elo && /^\d{4}-\d{2}-\d{2}$/.test(qDatum)) this.datum = qDatum;
    if (elo) {
      await this.tanuloValaszt(elo);
      const kod = String(qp.get('ok_kod') || '');
      if (this.okok.some((o) => o.kod === kod)) this.okKod = kod;
    }
  }

  get intezmenyPath(): string { return encodeURI(this.intezmeny); }

  async tanulokBetoltese() {
    this.betoltes = true;
    this.hiba = '';
    try {
      const lista: any = await firstValueFrom(
        this.http.get<any[]>(`/api/nyilvantartas/tanulo/${this.intezmenyPath}`));
      this.tanulok = (Array.isArray(lista) ? lista : []).map((t: any) => ({
        oktazon: String(t.oktazon),
        nev: String(t.full_name || '').trim() || String(t.oktazon),
        osztaly: t.aktualis_osztaly || null,
      })).sort((a, b) => a.nev.localeCompare(b.nev, 'hu'));
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A tanulók betöltése nem sikerült.';
    } finally {
      this.betoltes = false;
    }
  }

  get szurtTanulok(): any[] {
    const k = this.tanuloSzuro.trim().toLowerCase();
    if (!k) return this.tanulok.slice(0, 50);
    return this.tanulok
      .filter((t) => `${t.nev} ${t.oktazon} ${t.osztaly || ''}`.toLowerCase().includes(k))
      .slice(0, 50);
  }

  async tanuloValaszt(t: any) {
    this.valasztottTanulo = t;
    this.tetelekNyitva = false;
    await this.elemez();
  }

  masikTanulo() {
    this.valasztottTanulo = null;
    this.elemzes = null;
    this.ok = '';
    this.okKod = '';
    this.atvevo = '';
    this.utaniTorles = false;
  }

  async elemez() {
    if (!this.valasztottTanulo) { this.elemzes = null; return; }
    this.betoltes = true;
    this.hiba = '';
    try {
      const p = new URLSearchParams({ oktazon: this.valasztottTanulo.oktazon, datum: this.datum });
      this.elemzes = await firstValueFrom(this.http.get(
        `/api/tanulo-kilepes/elemzes/${this.intezmenyPath}?${p.toString()}`));
      //  Ha van a távozás után jelölés, alapból felajánljuk a levételét – de nem
      //  csendben: a képernyő kiírja, hány óráról van szó.
      this.utaniTorles = (this.elemzes?.kilepes_utani || []).length > 0;
    } catch (e: any) {
      this.hiba = e?.error?.error || 'Az elemzés nem sikerült.';
      this.elemzes = null;
    } finally {
      this.betoltes = false;
    }
  }

  tipusNev(id: any): string {
    return (this.elemzes?.tipusok || []).find((t: any) => String(t.id) === String(id))?.nev || String(id);
  }

  get tipusSorok(): any[] {
    const o = this.elemzes?.tipus_osszeg || {};
    return Object.keys(o).map((id) => ({ id, nev: this.tipusNev(id), db: o[id] }))
      .sort((a, b) => b.db - a.db);
  }

  /*  A kimutatás letöltése tokent igénylő végpontról jön, ezért a DataService
   *  megnyitóján megy – a sima <a href> nem visz jegyet, és 403-at kapna. */
  kimutatasLetoltes() {
    if (!this.valasztottTanulo) return;
    const p = new URLSearchParams({ oktazon: this.valasztottTanulo.oktazon });
    this.data.megnyitFajl(`/api/tanulo-kilepes/kimutatas/${this.intezmenyPath}?${p.toString()}`);
  }

  async vegrehajt() {
    if (!this.valasztottTanulo || !this.elemzes) return;

    const rendezetlen = Number(this.elemzes?.rendezetlen || 0);
    const utana = (this.elemzes?.kilepes_utani || []).length;
    const sorok = [
      `${this.valasztottTanulo.nev} kiléptetése ${this.datum} nappal.`,
      '',
      `Lezárul: ${this.elemzes?.elo_jogviszony || 0} jogviszony, `
        + `${(this.elemzes?.csoportok || []).length} csoporttagság.`,
      'A jegyek, a hiányzások és a feljegyzések MEGMARADNAK — nem törlünk semmit.',
    ];
    if (rendezetlen) {
      sorok.push('', `FIGYELEM: még ${rendezetlen} óra rendezetlen (igazolatlan vagy elbírálatlan).`,
        'Ezeket az e-KRÉTÁ-ba így, rendezetlenül kell átvezetni.');
    }
    if (this.utaniTorles && utana) {
      sorok.push('', `${utana} óra hiányzás-jelölése törlődik a távozás napja UTÁNRÓL.`);
    }
    if (!confirm(`${sorok.join('\n')}\n\nVégrehajtod?`)) return;

    this.fut = true;
    try {
      const r: any = await firstValueFrom(this.http.post(
        `/api/tanulo-kilepes/vegrehajt/${this.intezmenyPath}`,
        { oktazon: this.valasztottTanulo.oktazon, datum: this.datum,
          ok: this.ok, ok_kod: this.okKod || undefined, atvevo: this.atvevo || undefined,
          kilepes_utani_torles: this.utaniTorles }));
      this.data.sendToast(
        `Kiléptetve ${r?.datum}-val. ${r?.jogviszony || 0} jogviszony és `
        + `${r?.csoport || 0} csoporttagság lezárva`
        + (r?.hianyzas_torolve ? `, ${r.hianyzas_torolve} utólagos jelölés levéve` : '') + '.',
        'success');
      if (r?.iskolavaltas && !r?.atado?.keszult) {
        this.data.sendToast('Iskolaváltás: az átadó csomag (Nkt. 41. § (8a)) még nem készült el – az „Átadó csomag készítése” gombbal pótolható.', 'warning');
      }
      await this.elemez();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A kiléptetés nem sikerült.', 'error');
    } finally {
      this.fut = false;
    }
  }
}
